import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  X,
  Check,
  CheckCheck,
  Trash2,
  Volume2,
  VolumeX,
  AlertTriangle,
  Info,
  AlertCircle,
  CheckCircle,
  Settings,
  BellRing,
  BellOff,
  Play,
  Smartphone,
  Gauge,
  MapPin,
  WifiOff,
  Battery,
  AlertOctagon,
  Wrench,
} from 'lucide-react';
import {
  useNotificationsStore,
  Notification,
  NotificationSoundType,
} from '../store/notifications.store';
import { formatDistanceToNow } from 'date-fns';
import { fr, de, enUS, it } from 'date-fns/locale';
import clsx from 'clsx';

const DATE_LOCALES: Record<string, typeof fr> = { fr, de, en: enUS, it };

const SOUND_TYPE_KEYS: Record<NotificationSoundType, string> = {
  default: 'notification_panel.sound_default',
  chime: 'notification_panel.sound_chime',
  bell: 'notification_panel.sound_bell',
  alert: 'notification_panel.sound_alert',
  none: 'notification_panel.sound_none',
};

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const typeIcons: Record<Notification['type'], React.ElementType> = {
  alert: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle,
};

const typeColors: Record<Notification['type'], string> = {
  alert: 'text-red-600 bg-red-100',
  warning: 'text-amber-600 bg-amber-100',
  info: 'text-blue-600 bg-blue-100',
  success: 'text-green-600 bg-green-100',
};

type TabType = 'notifications' | 'settings';

function NotificationItem({
  notification,
  onMarkAsRead,
  onRemove,
}: {
  notification: Notification;
  onMarkAsRead: () => void;
  onRemove: () => void;
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = DATE_LOCALES[i18n.language?.split('-')[0] ?? 'fr'] ?? fr;
  const Icon = typeIcons[notification.type];
  const colorClass = typeColors[notification.type];

  return (
    <div
      className={clsx(
        'p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors',
        !notification.read && 'bg-green-50/50'
      )}
    >
      <div className="flex items-start gap-3">
        <div className={clsx('p-2 rounded-lg', colorClass)}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4 className={clsx('font-medium text-sm', !notification.read ? 'text-gray-900' : 'text-gray-500')}>
              {notification.title}
            </h4>
            <span className="text-xs text-gray-400 whitespace-nowrap">
              {formatDistanceToNow(new Date(notification.timestamp), {
                addSuffix: true,
                locale: dateLocale,
              })}
            </span>
          </div>
          <p className={clsx('text-sm mt-0.5', !notification.read ? 'text-gray-600' : 'text-gray-400')}>
            {notification.message}
          </p>
          <div className="flex items-center gap-2 mt-2">
            {!notification.read && (
              <button
                onClick={onMarkAsRead}
                className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
              >
                <Check className="w-3 h-3" />
                {t('notification_panel.mark_read')}
              </button>
            )}
            <button
              onClick={onRemove}
              className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" />
              {t('notification_panel.remove')}
            </button>
          </div>
        </div>
        {!notification.read && (
          <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
        )}
      </div>
    </div>
  );
}

function SettingsTab() {
  const { t } = useTranslation();
  const {
    soundEnabled,
    soundType,
    pushEnabled,
    pushPermission,
    preferences,
    toggleSound,
    setSoundType,
    requestPushPermission,
    setPushEnabled,
    updatePreferences,
  } = useNotificationsStore();

  const soundTypes: NotificationSoundType[] = ['default', 'chime', 'bell', 'alert', 'none'];

  const alertTypeSettings = [
    { key: 'overspeed' as const, label: t('notification_panel.alert_overspeed'), icon: Gauge },
    { key: 'geofence' as const, label: t('notification_panel.alert_geofence'), icon: MapPin },
    { key: 'offline' as const, label: t('notification_panel.alert_offline'), icon: WifiOff },
    { key: 'battery' as const, label: t('notification_panel.alert_battery'), icon: Battery },
    { key: 'sos' as const, label: t('notification_panel.alert_sos'), icon: AlertOctagon },
    { key: 'maintenance' as const, label: t('notification_panel.alert_maintenance'), icon: Wrench },
  ];

  return (
    <div className="p-4 space-y-6">
      {/* Push Notifications */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-green-600" />
          {t('notification_panel.push_section')}
        </h4>
        <div className="bg-gray-50 rounded-xl p-3 space-y-3">
          {pushPermission === 'unsupported' ? (
            <p className="text-sm text-gray-500">
              {t('notification_panel.push_unsupported')}
            </p>
          ) : pushPermission === 'denied' ? (
            <div>
              <p className="text-sm text-amber-600 mb-2">
                {t('notification_panel.push_blocked_title')}
              </p>
              <p className="text-xs text-gray-500">
                {t('notification_panel.push_blocked_subtitle')}
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-700">{t('notification_panel.push_toggle_title')}</p>
                <p className="text-xs text-gray-500">{t('notification_panel.push_toggle_subtitle')}</p>
              </div>
              <button
                onClick={() => {
                  if (pushPermission === 'granted') {
                    setPushEnabled(!pushEnabled);
                  } else {
                    requestPushPermission();
                  }
                }}
                className={clsx(
                  'relative w-11 h-6 rounded-full transition-colors',
                  pushEnabled ? 'bg-green-500' : 'bg-gray-300'
                )}
              >
                <span
                  className={clsx(
                    'absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow-lg',
                    pushEnabled ? 'left-5' : 'left-0.5'
                  )}
                />
              </button>
            </div>
          )}
          {pushPermission === 'default' && (
            <button
              onClick={requestPushPermission}
              className="w-full py-2 px-3 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200 transition-colors flex items-center justify-center gap-2"
            >
              <BellRing className="w-4 h-4" />
              {t('notification_panel.push_authorize')}
            </button>
          )}
        </div>
      </div>

      {/* Sound Settings */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
          <Volume2 className="w-4 h-4 text-purple-600" />
          {t('notification_panel.sound_section')}
        </h4>
        <div className="bg-gray-50 rounded-xl p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700">{t('notification_panel.sound_toggle_title')}</p>
              <p className="text-xs text-gray-500">{t('notification_panel.sound_toggle_subtitle')}</p>
            </div>
            <button
              onClick={toggleSound}
              className={clsx(
                'relative w-11 h-6 rounded-full transition-colors',
                soundEnabled ? 'bg-green-500' : 'bg-gray-300'
              )}
            >
              <span
                className={clsx(
                  'absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow-lg',
                  soundEnabled ? 'left-5' : 'left-0.5'
                )}
              />
            </button>
          </div>

          {soundEnabled && (
            <div className="pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">{t('notification_panel.sound_type')}</p>
              <div className="grid grid-cols-2 gap-2">
                {soundTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => setSoundType(type)}
                    className={clsx(
                      'py-2 px-3 rounded-lg text-sm flex items-center justify-between transition-colors',
                      soundType === type
                        ? 'bg-green-100 text-green-700 border border-green-300'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                    )}
                  >
                    <span>{t(SOUND_TYPE_KEYS[type])}</span>
                    {type !== 'none' && soundType === type && (
                      <Play className="w-3 h-3" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alert Type Preferences */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
          <Bell className="w-4 h-4 text-amber-500" />
          {t('notification_panel.alert_types_section')}
        </h4>
        <div className="bg-gray-50 rounded-xl divide-y divide-gray-200">
          {alertTypeSettings.map(({ key, label, icon: Icon }) => (
            <div key={key} className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">{label}</span>
              </div>
              <button
                onClick={() => updatePreferences({ [key]: !preferences[key] })}
                className={clsx(
                  'relative w-9 h-5 rounded-full transition-colors',
                  preferences[key] ? 'bg-green-500' : 'bg-gray-300'
                )}
              >
                <span
                  className={clsx(
                    'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-lg',
                    preferences[key] ? 'left-4' : 'left-0.5'
                  )}
                />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function NotificationPanel({ isOpen, onClose }: NotificationPanelProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('notifications');
  const {
    notifications,
    unreadCount,
    soundEnabled,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAll,
    toggleSound,
  } = useNotificationsStore();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-x-0 top-16 bottom-0 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[400px] sm:max-h-[calc(100vh-100px)] sm:rounded-xl bg-white sm:border sm:border-gray-200 shadow-2xl overflow-hidden z-50">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-green-600" />
            <h3 className="font-semibold text-gray-900">{t('notification_panel.header_title')}</h3>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleSound}
              className={clsx(
                'p-1.5 rounded-lg transition-colors',
                soundEnabled
                  ? 'text-green-600 hover:bg-green-100'
                  : 'text-gray-400 hover:bg-gray-200'
              )}
              title={soundEnabled ? t('notification_panel.sound_disable_title') : t('notification_panel.sound_enable_title')}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setActiveTab('notifications')}
            className={clsx(
              'flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2',
              activeTab === 'notifications'
                ? 'bg-green-100 text-green-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            )}
          >
            <BellRing className="w-4 h-4" />
            {t('notification_panel.tab_alerts')}
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={clsx(
              'flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2',
              activeTab === 'settings'
                ? 'bg-green-100 text-green-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            )}
          >
            <Settings className="w-4 h-4" />
            {t('notification_panel.tab_settings')}
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'notifications' ? (
        <>
          {/* Quick actions */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-3">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  {t('notification_panel.mark_all_read')}
                </button>
              )}
              <button
                onClick={clearAll}
                className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {t('notification_panel.clear_all')}
              </button>
            </div>
          )}

          {/* Notifications list */}
          <div className="overflow-y-auto max-h-[400px]">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <BellOff className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">{t('notification_panel.empty_title')}</p>
                <p className="text-sm text-gray-400 mt-1">
                  {t('notification_panel.empty_subtitle')}
                </p>
              </div>
            ) : (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={() => markAsRead(notification.id)}
                  onRemove={() => removeNotification(notification.id)}
                />
              ))
            )}
          </div>
        </>
      ) : (
        <div className="overflow-y-auto max-h-[500px]">
          <SettingsTab />
        </div>
      )}
    </div>
  );
}
